"use client";

import { usePrivy } from "@privy-io/react-auth";
import { getEnvChainById } from "data/rpc";
import { useDevtools } from "logic/components/Devtools";
import { useIsMounted, usePrivyUserMetadata } from "logic/hooks";
import { createContext, type PropsWithChildren, useContext, useEffect } from "react";
import { http, type Address, createPublicClient, createWalletClient } from "viem";
import { arbitrum } from "viem/chains";
import { createConnector, useChains, useConnect, useConnection, useDisconnect } from "wagmi";

export const SIGNER_CONNECTOR_ID = "SIGNER_CONNECTOR";
export const IMPERSONATOR_CONNECTOR_ID = "IMPERSONATOR_CONNECTOR";
export const createCustomConnector = (accounts: Address[], chainId: number, id: string = SIGNER_CONNECTOR_ID) => {
  return createConnector((config) => {
    return {
      id,
      name: "Signer Connector",
      type: "wallet",
      disconnect: async () => {
        config.emitter.emit("disconnect");
      },
      onDisconnect: async () => {
        config.emitter.emit("disconnect");
      },
      onConnect: async () => {
        config.emitter.emit("connect", {
          accounts: accounts,
          chainId: chainId,
        });
      },
      async connect({ withCapabilities } = {}) {
        config.emitter.emit("connect", {
          accounts,
          chainId: chainId,
        });

        return {
          accounts: (withCapabilities ? accounts.map((address) => ({ address, capabilities: {} })) : accounts) as never,
          chainId: chainId,
        };
      },
      getAccounts: async () => {
        return accounts;
      },
      getChainId: async () => {
        return chainId;
      },
      getProvider: async () => {
        const publicClient = createPublicClient({
          transport: http("/api/rpc", { fetchOptions: { headers: { chainid: `${chainId}` } } }),
          chain: getEnvChainById(chainId),
          name: "Additional Signer",
        });

        return publicClient;
      },
      getClient: async () => {
        const walletClient = createWalletClient({
          account: accounts[0],
          transport: http("/api/rpc", { fetchOptions: { headers: { chainid: `${chainId}` } } }),
          chain: getEnvChainById(chainId),
          name: "Additional Signer",
        });

        return walletClient;
      },
      isAuthorized: async () => {
        return true;
      },
      onAccountsChanged: async (accounts: Address[]) => {
        config.emitter.emit("change", { accounts });
      },
      onChainChanged: async (chainId: string) => {
        config.emitter.emit("change", { chainId: parseInt(chainId) });
      },
      switchChain: async ({ chainId }) => {
        config.emitter.emit("change", { chainId: chainId });
        const chain = getEnvChainById(chainId);
        if (!chain) throw new Error(`Chain ${chainId} not supported`);
        return chain;
      },
    };
  });
};

function useWeb3State() {
  const isMounted = useIsMounted();
  const { authenticated, user, ready } = usePrivy();
  const userMetadata = usePrivyUserMetadata();

  const { impersonatedAddress } = useDevtools();

  const { address: activeAddress, chain: activeChain, connector: activeConnector, ...accountRest } = useConnection();
  const { mutate: connect } = useConnect();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    console.log("[impersonatedAddress]", impersonatedAddress, {
      connected: accountRest.isConnected,
      connecting: accountRest.isConnecting,
    });

    if (impersonatedAddress) {
      if (
        accountRest.isConnected &&
        activeConnector?.id === IMPERSONATOR_CONNECTOR_ID &&
        activeAddress === impersonatedAddress
      )
        return;
      const connector = createCustomConnector([impersonatedAddress], arbitrum.id, IMPERSONATOR_CONNECTOR_ID);
      connect({ connector });
      return;
    }

    // Impersonation was cleared — disconnect the impersonator connector so the real wallet can reconnect.
    if (accountRest.isConnected && activeConnector?.id === IMPERSONATOR_CONNECTOR_ID) {
      disconnect();
      return;
    }

    if (accountRest.isConnected || accountRest.isConnecting) return;

    if (!ready || !authenticated || !userMetadata) return;
    if (userMetadata.linkedWallets.length === 0) return;

    const accounts = userMetadata.linkedWallets.map((w) => w.address);
    const connector = createCustomConnector([accounts[0]], arbitrum.id);
    connect({ connector });
  }, [
    ready,
    authenticated,
    userMetadata,
    connect,
    disconnect,
    accountRest.isConnected,
    accountRest.isConnecting,
    activeConnector,
    activeAddress,
    impersonatedAddress,
  ]);

  const chains = useChains();
  const chain = activeChain ?? chains[0];

  let address: Address | undefined;
  if (isMounted) {
    if (authenticated) {
      const firstLinkedWallet = user?.linkedAccounts.find((a) => a.type == "wallet")?.address as Address | undefined;
      address = activeAddress ?? firstLinkedWallet;
    }
  }

  if (impersonatedAddress) address = impersonatedAddress;

  const isAdditionalSigner =
    Boolean(userMetadata?.linkedWallets.find((w) => w.address === address)) || Boolean(impersonatedAddress);

  return {
    account: {
      ...accountRest,
      chain: activeChain,
      address,
      ready,
    },
    address,
    chain,
    chainId: chain.id,
    isAdditionalSigner,
  };
}

const Web3Context = createContext<ReturnType<typeof useWeb3State> | undefined>(undefined);

export function useWeb3() {
  const context = useContext(Web3Context);
  if (!context) throw new Error("useWeb3 must be used within a Web3Provider");
  return context;
}

export function Web3Provider(props: PropsWithChildren) {
  const state = useWeb3State();

  return <Web3Context.Provider value={state}>{props.children}</Web3Context.Provider>;
}
